import { Storage } from './storage';
import { STORAGE_KEYS, type DimensionScopeKeyParts } from './storageKeys';

/**
 * Persistence for the active dimension's value selection (DP-3188).
 *
 * The URL stays the render-time source of truth; this layer only remembers a
 * committed selection so a later session can hydrate it (URL → storage →
 * picker). It sits on `Storage`, which self-probes `localStorage` and degrades
 * every operation to a no-op when it is unavailable — consumers need no
 * try/catch of their own, they just fall back to a per-session picker.
 *
 * Two invariants this module owns:
 *
 * - **The all-selected state is a sentinel, not a list.** `__ALL__` is stored
 *   instead of every id, so a value added to the dimension later is included
 *   automatically. It never round-trips through the URL, which stays paramless
 *   for all-selected (`accessDimensionQueryParam.ts`).
 * - **An empty selection is an absent entry, never a stored empty array.**
 *   "Nothing persisted yet" is what triggers the first-login picker, so
 *   writing `[]` would record a state the resolver cannot act on. `write`
 *   clears instead.
 */

/** Stands in for "every value selected", including ones added later. */
export const ALL_DIMENSION_VALUES = '__ALL__';

/** Bumped only alongside a `DimensionScopeValue` shape change. */
const DIMENSION_SCOPE_VERSION = 1;

export type DimensionScopeValue =
  | { v: typeof DIMENSION_SCOPE_VERSION; values: string[] }
  | { v: typeof DIMENSION_SCOPE_VERSION; values: typeof ALL_DIMENSION_VALUES };

/**
 * Reject anything this version cannot act on: an unknown `v`, the empty
 * array that must never have been written, and hand-edited junk. Entries from
 * a later shape are dropped rather than migrated — the `v` field exists so
 * that migration can be added deliberately.
 */
function isDimensionScopeValue(
  candidate: unknown
): candidate is DimensionScopeValue {
  if (typeof candidate !== 'object' || candidate === null) {
    return false;
  }
  const { v, values } = candidate as Partial<DimensionScopeValue>;
  if (v !== DIMENSION_SCOPE_VERSION) {
    return false;
  }
  if (values === ALL_DIMENSION_VALUES) {
    return true;
  }
  return (
    Array.isArray(values) &&
    values.length > 0 &&
    values.every(value => typeof value === 'string')
  );
}

/**
 * @returns the persisted selection, or `null` when nothing usable is stored —
 *   absent entry, unavailable storage, malformed JSON, or a `v` this version
 *   does not understand. Never throws.
 */
export function readDimensionScope(
  parts: DimensionScopeKeyParts
): DimensionScopeValue | null {
  try {
    const raw = Storage.get(STORAGE_KEYS.dimensionScope(parts));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isDimensionScopeValue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Persist a committed selection. An empty `values` array clears the entry
 * instead of writing it, so "no selection" stays indistinguishable from
 * "never chose". No-op when `localStorage` is unavailable.
 */
export function writeDimensionScope({
  value,
  ...parts
}: DimensionScopeKeyParts & { value: DimensionScopeValue }): void {
  if (Array.isArray(value.values) && value.values.length === 0) {
    clearDimensionScope(parts);
    return;
  }
  Storage.set(STORAGE_KEYS.dimensionScope(parts), JSON.stringify(value));
}

/** Drop the persisted selection, returning the user to the picker. */
export function clearDimensionScope(parts: DimensionScopeKeyParts): void {
  Storage.remove(STORAGE_KEYS.dimensionScope(parts));
}
